import { CaseReducer, SliceCaseReducers } from '@reduxjs/toolkit';
import { createSlice } from 'helpers/createSlice';
import { pushStringToUniqueArray } from 'helpers/general';
import { loadField, updateField } from 'helpers/localStorage';
import { ActionWithPayload, MakerSelectors } from 'types';

const developingField = 'developing';

type State = {
  backendEnv: 'dev' | 'gamma' | 'prod';
  frontendEnv: 'development' | 'production' | 'test';
  developing: Array<string>;
};

const initialState: State = {
  backendEnv: process.env.KODS_APP_ENV as State['backendEnv'],
  frontendEnv: process.env.NODE_ENV as State['frontendEnv'],
  developing: loadField({ field: developingField }) || [],
};

interface Reducers extends SliceCaseReducers<State> {
  reset: CaseReducer<State>;
  pushToDeveloping: CaseReducer<State, ActionWithPayload<string>>;
  removeFromDeveloping: CaseReducer<State, ActionWithPayload<string>>;
}

interface ExtraSelectors extends MakerSelectors<State> {
  getIsDevBackend: (state: State) => boolean;
  getIsGammaBackend: (state: State) => boolean;
  getIsProdBackend: (state: State) => boolean;
  getIsDeveloping: (state: State) => boolean;
  getHasDeveloping: (state: State) => (id: string) => boolean;
}

export const baseConfig = createSlice<State, Reducers, ExtraSelectors>({
  nestedPath: [],
  name: 'baseConfig',
  initialState,
  reducers: {
    reset: () => initialState,
    pushToDeveloping: (state, { payload: id }) => {
      const newDeveloping = [...state.developing];
      pushStringToUniqueArray(id, newDeveloping);

      state.developing = newDeveloping;
      updateField({ field: developingField, data: newDeveloping });
    },
    removeFromDeveloping: (state, { payload: id }) => {
      const newDeveloping = [...state.developing];

      const index = newDeveloping.findIndex((idx) => idx === id);
      if (index >= 0) {
        newDeveloping.splice(index, 1);

        state.developing = newDeveloping;
        updateField({ field: developingField, data: newDeveloping });
      }
    },
  },
  extraSelectors: {
    getIsDevBackend: ({ backendEnv }) => backendEnv === 'dev',
    getIsGammaBackend: ({ backendEnv }) => backendEnv === 'gamma',
    getIsProdBackend: ({ backendEnv }) => backendEnv === 'prod',
    getIsDeveloping: ({ frontendEnv }) => frontendEnv === 'development',
    getHasDeveloping:
      ({ developing }) =>
      (id) =>
        developing.includes(id),
  },
});
